import * as Comlink from 'comlink';
import type { Remote } from 'comlink';
import { ApplyFilters as ApplyFiltersSync } from '../hoc/MarketListings/marketListingsFilter';
import type { BrokerNerdsBarkerCoreModelsMarketplacesListing, BrokerNerdsBarkerCoreModelsPricingRuleFilters } from '../api';
import type { MarketListingsFilterWorkerApi } from './marketListingsFilterWorker';

type Listing = BrokerNerdsBarkerCoreModelsMarketplacesListing;

let remote: Remote<MarketListingsFilterWorkerApi> | null = null;

// The worker is created lazily on first use so it isn't spun up for sessions
// that never touch market listings (and so it stays out of the critical path
// during initial app boot).
function getRemote(): Remote<MarketListingsFilterWorkerApi> {
  if (!remote) {
    const worker = new Worker(new URL('./marketListingsFilterWorker.ts', import.meta.url), {
      type: 'module',
      name: 'market-listings-filter',
    });
    remote = Comlink.wrap<MarketListingsFilterWorkerApi>(worker);
  }
  return remote;
}

// Keep the main-thread registry from outgrowing the worker's resident set.
// Mirrors the worker's own MAX_DATASETS bound.
const MAX_KEYED_DATASETS = 500;

type DatasetHandle = { id: string; length: number; registration: Promise<void> };

// Keyless path — "pass once" by array *reference*. As long as the same array
// instance is filtered (the common case while only filters change), the array
// is shipped to the worker a single time and later calls send only the filter
// params. Each distinct array gets a globally-unique id, so the indices the
// worker returns are always rehydrated from the very array they were computed
// against. The WeakMap lets the handle be collected once the source array is
// unreachable; the worker bounds its own copies via LRU.
const refRegistry = new WeakMap<Listing[], DatasetHandle>();
let nextRefId = 1;

// Keyed path — datasets identified by a caller-supplied composite key
// (tenant|marketplace|event|asOfDate). A re-created array carrying the same key
// reuses the worker's resident copy, so the array crosses the boundary once per
// (key, length) rather than once per array reference. This trades the keyless
// path's per-instance safety for cross-instance reuse and relies on the caller's
// contract that an identical key implies identical contents & order (asOfDate is
// the data version, so new data yields a new key). The length guard re-registers
// on the one divergence we can cheaply detect; bounded LRU so this map can't
// outgrow the worker's resident set.
const keyedRegistry = new Map<string, DatasetHandle>();

function touchKeyed(key: string, handle: DatasetHandle) {
  keyedRegistry.delete(key);
  keyedRegistry.set(key, handle);
  while (keyedRegistry.size > MAX_KEYED_DATASETS) {
    const oldest = keyedRegistry.keys().next().value as string | undefined;
    if (oldest === undefined) {
      break;
    }
    keyedRegistry.delete(oldest);
  }
}

function registerRef(api: Remote<MarketListingsFilterWorkerApi>, listings: Listing[]): DatasetHandle {
  const id = `ref:${nextRefId++}`;
  const registration = api.registerDataset(id, listings).then(() => undefined);
  const handle: DatasetHandle = { id, length: listings.length, registration };
  refRegistry.set(listings, handle);
  return handle;
}

function registerKeyed(api: Remote<MarketListingsFilterWorkerApi>, key: string, listings: Listing[]): DatasetHandle {
  const registration = api.registerDataset(key, listings).then(() => undefined);
  const handle: DatasetHandle = { id: key, length: listings.length, registration };
  touchKeyed(key, handle);
  return handle;
}

// Run the index round-trip for an already-resolved handle, re-registering once
// if the dataset was evicted from the worker's LRU between registration and use.
async function filterByHandle(
  api: Remote<MarketListingsFilterWorkerApi>,
  handle: DatasetHandle,
  reRegister: () => DatasetHandle,
  filters: BrokerNerdsBarkerCoreModelsPricingRuleFilters,
  includeOwnListings: boolean,
  includeIgnoredListings: boolean,
  includeOutliers: boolean,
): Promise<number[] | null> {
  await handle.registration;
  let indices = await api.applyFiltersByIndex(handle.id, filters, includeOwnListings, includeIgnoredListings, includeOutliers);
  if (indices === null) {
    // Evicted from the worker's LRU — re-register this exact array and retry once.
    const fresh = reRegister();
    await fresh.registration;
    indices = await api.applyFiltersByIndex(fresh.id, filters, includeOwnListings, includeIgnoredListings, includeOutliers);
  }
  return indices;
}

/**
 * Off-thread equivalent of {@link ApplyFiltersSync}. The listings array crosses
 * the worker boundary at most once per array reference; only the matching
 * indices come back, and the result objects are rehydrated from the local array
 * here on the main thread. Mirrors ApplyFilters' argument order and semantics.
 *
 * Pass `datasetKey` only when the array's contents are fully determined by that
 * key (e.g. raw market listings keyed by tenant|marketplace|event|asOfDate).
 * Doing so lets the worker reuse a single resident copy across distinct array
 * instances that share the key — the array is then sent at most once per
 * (key, length) instead of once per reference. Omit it (the default) for any
 * array whose contents depend on more than the key, such as already-filtered or
 * mapped lists, to stay on the collision-safe reference-keyed path.
 */
export async function applyFiltersAsync(
  listings: Listing[] | null | undefined,
  filters: BrokerNerdsBarkerCoreModelsPricingRuleFilters,
  includeOwnListings = false,
  includeIgnoredListings = false,
  includeOutliers = false,
  datasetKey?: string,
): Promise<Listing[]> {
  if (!listings || listings.length === 0) {
    return [];
  }

  const api = getRemote();

  let handle: DatasetHandle;
  let reRegister: () => DatasetHandle;
  if (datasetKey) {
    const existing = keyedRegistry.get(datasetKey);
    // Reuse the resident copy only when the lengths still agree; a different
    // length means the key was reused for resized data, so re-register to keep
    // the returned indices valid against the caller's array.
    if (existing && existing.length === listings.length) {
      touchKeyed(datasetKey, existing);
      handle = existing;
    } else {
      handle = registerKeyed(api, datasetKey, listings);
    }
    reRegister = () => registerKeyed(api, datasetKey, listings);
  } else {
    handle = refRegistry.get(listings) ?? registerRef(api, listings);
    reRegister = () => registerRef(api, listings);
  }

  const indices = await filterByHandle(api, handle, reRegister, filters, includeOwnListings, includeIgnoredListings, includeOutliers);

  if (indices === null) {
    // Should be unreachable (we just registered). Stay correct over fast.
    return ApplyFiltersSync(listings, filters, includeOwnListings, includeIgnoredListings, includeOutliers);
  }

  const out: Listing[] = new Array(indices.length);
  for (let i = 0; i < indices.length; i++) {
    out[i] = listings[indices[i]];
  }
  return out;
}
